import { useEffect, useRef } from 'react';

import type { InboxPage } from '@/services/conversations/inbox';
import { Sentry } from '@/util/sentry';

type UseFillGroupedInboxListParams = {
  /** True while the list shows inquiry group cards. */
  enabled: boolean;
  /** The tab's loaded pages. Pages the server grouped turn the hook off. */
  pages: readonly (InboxPage | undefined)[] | undefined;
  /** Rows the list draws: cards and flat rows together. */
  rowCount: number;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
};

/** Enough rows to fill a tall screen, so the list can scroll and load the rest itself. */
const MIN_ROWS = 10;

/** A ceiling on pages pulled without the reader scrolling, so a sparse inbox stops asking. */
const MAX_AUTO_FETCHES = 5;

/**
 * Keeps loading pages while client grouping leaves the list too short to scroll.
 *
 * Grouping folds a page's threads into a few cards, so a page of ten threads can draw three rows.
 * The list asks for the next page only when the reader scrolls near its end, and a list shorter
 * than the screen cannot scroll, so it stopped after the first page with the rest of the inbox
 * unreachable. Server grouping pages by question and does not need this.
 */
export function useFillGroupedInboxList({
  enabled: isGrouped,
  pages,
  rowCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseFillGroupedInboxListParams) {
  const autoFetchesRef = useRef(0);

  const enabled = isGrouped && !pages?.[0]?.questionGroups;

  useEffect(() => {
    if (!enabled || rowCount >= MIN_ROWS) {
      autoFetchesRef.current = 0;

      return;
    }

    if (!hasNextPage || isFetchingNextPage || autoFetchesRef.current >= MAX_AUTO_FETCHES) {
      return;
    }

    autoFetchesRef.current += 1;

    fetchNextPage().catch((error) => {
      Sentry.captureException(error, {
        tags: { scope: 'inbox', hook: 'useFillGroupedInboxList' },
        extra: { rowCount, autoFetches: autoFetchesRef.current },
      });
    });
  }, [enabled, rowCount, hasNextPage, isFetchingNextPage, fetchNextPage]);
}
